import {
  AfterViewInit,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Calendar} from 'primeng/calendar';
import {DateFormatPipe} from 'ngx-moment';
import * as moment from 'moment';
import {unitOfTime} from 'moment';

import {GenericFunctionsUtil} from '../../utils/generic-functions.util';
import {ValidationService} from '../../services/validation.service';
import {ResponsiveService} from '../../services/responsive.service';

const DEFAULT_MIN_DATE = -2840139916000; // 01/01/1880

@Component({
  selector: 'si-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DatepickerComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() id: string;
  @Input() name: string;
  @Input() text: string;
  @Input() styleClass: string;
  @Input() disabled: boolean;
  @Input() rule: string;
  @Input() validationExternalErrorText: string;

  // If true, the datepicker does not take the user's time zone into account. Instead, it uses the time in UTC
  @Input() isUTC = false;
  @Input() format = 'L';
  @Input() startOf: string;
  @Input() endOf: string;
  @Input() view = 'date';
  // Shows the date and the time in the datepicker
  @Input() showTime: boolean;
  // Shows a button that sets the current date and time
  @Input() showToday = false;
  // Shows an icon that sets the current date and time
  @Input() showTodayIcon = false;
  // Shows only the time in the datepicker
  @Input() timeOnly: boolean;
  // Sets the model empty by default
  @Input() empty: boolean;
  @Input() hideLabel = false;
  @Input() showClear = true;
  @Input() readonlyInput = false;
  @Input() tooltipText: string;
  @Input() tooltipPosition: string;
  @Input() tooltipIcon: string;
  @Input() tooltipStyleClass: string;
  @Input() tooltipEscape = true;
  @Input() forceShowTooltipIcon: boolean;
  @Input() stepMinute: number = 1;
  @Input() previousNextModeUnit = 'day' as unitOfTime.Base;
  @Output() modelChange = new EventEmitter<any>();
  @Output() modelSelected = new EventEmitter<any>();
  @Output() tooltipIconClick = new EventEmitter<any>();
  @ViewChild('pCalendar', {static: true}) pCalendar: Calendar;
  showRuleErrorOutsideIcon: boolean;
  validationErrorText: string;
  // model of the pCalendar
  modelDate: Date;
  // default date of the pCalendar
  defaultDate: Date;
  yearRange = `${new Date().getFullYear() - 100}:${new Date().getFullYear() + 5}`;
  placeholderTranslated: string;
  firstDayOfWeek: number;
  defaultTimeSetFlag = false;
  isPreviousButtonAvailable: boolean;
  isNextButtonAvailable: boolean;
  isDesktop: boolean;
  private _listeners = [];
  private clearIconElement: HTMLElement;
  // Model of datepicker (milliseconds)
  private _model: number;
  private _placeholder: string;
  private _minDate = this.parseMsToDate(DEFAULT_MIN_DATE);
  private _maxDate;
  // The available dates in the pCalendar are from the current day and time.
  private _onlyFuture;
  // The available dates in the pCalendar are until the 23:59 of the current day.
  private _pastOrToday;
  private _previousNextMode = false;

  constructor(
    private renderer: Renderer2,
    private translateService: TranslateService,
    private validationService: ValidationService,
    private responsiveService: ResponsiveService
  ) {
    this.setLogic();
    this.setDatepickerI18nConfig();
  }

  get model() {
    return this._model;
  }

  @Input()
  set model(val) {
    // Avoid setting the same value and emit the modelChange twice
    if (val === this.model) {
      return;
    }

    // Assign modelDate of pCalendar.
    const isValid = !!this.validateDate(this.setTimeInDate(this.parseMsToDate(val)));
    const previousValue = this._model;

    if (!val) {
      this._model = null;
      this.modelDate = null;
      this.displayClearIcon(false);
    } else {
      if (isValid && val !== this.modelDate?.getTime()) {
        this.modelDate = this.setTimeInDate(this.parseMsToDate(val));
        this.displayClearIcon();
      }
    }

    // Assign model of datepicker and emit new value when previous model is undefined (because maybe the time is different).
    // Example: when datepicker is initialized and received a date as a model, we need to emit its value with the correct time.
    if (isValid) {
      if (!this._model && val || (val && val !== this._model)) {
        this._model = this.parseDateToMs(this.setTimeInDate(this.parseMsToDate(val)));
      } else {
        this._model = val;
      }

      if (this._model !== previousValue) {
        setTimeout(() => {
          this.modelChange.emit(this._model);
        });
      }
    }

    this.checkPreviousButtonAvailability();
    this.checkNextButtonAvailability();
  }

  get placeholder() {
    return this._placeholder;
  }

  @Input()
  set placeholder(val) {
    this._placeholder = val;
    this.translateService.get(val).toPromise().then(translation => {
      this.placeholderTranslated = translation;
    });
  }

  get minDate() {
    return this._minDate;
  }

  @Input()
  set minDate(val) {
    this._minDate = this.parseMsToDate(val || DEFAULT_MIN_DATE);
    this.checkPreviousButtonAvailability();
    this.checkNextButtonAvailability();
  }

  get maxDate() {
    return this._maxDate;
  }

  @Input()
  set maxDate(val) {
    if (!val) {
      this._maxDate = val;
    } else {
      this._maxDate = this.parseMsToDate(val);
    }

    this.checkPreviousButtonAvailability();
    this.checkNextButtonAvailability();
  }

  get onlyFuture() {
    return this._onlyFuture;
  }

  @Input()
  set onlyFuture(val) {
    this._onlyFuture = val;
    if (this.showTime || this.timeOnly) {
      this.minDate = new Date(new Date().setSeconds(0, 0));
    } else {
      this.minDate = new Date(new Date().setHours(0, 0, 0, 0));
    }
  }

  get pastOrToday() {
    return this._pastOrToday;
  }

  @Input()
  set pastOrToday(val) {
    this._pastOrToday = val;
    if (this.showTime || this.timeOnly) {
      this.maxDate = new Date().getTime();
    } else {
      this.maxDate = new Date(moment().endOf('day').valueOf()).getTime();
    }
  }

  get previousNextMode() {
    return this._previousNextMode;
  }

  @Input()
  set previousNextMode(val) {
    this._previousNextMode = val;
    this.showClear = false;
  }

  @HostListener('click', ['$event'])
  public onClick(event: any): void {
    if (event.target.nodeName === 'A' || event.target.parentElement.nodeName === 'A') {
      event.stopPropagation();
    }
  }

  ngOnInit() {
    this.decorateCalendar();
    // Set default time in the calendar dropdown.
    this.defaultDate = this.setTimeInDate(new Date());
    // Set today in the model by default.
    if (!this.empty && !this.model) {
      this.modelDate = this.defaultDate;
      if (!this.model) {
        setTimeout(() => {
          this.model = this.parseDateToMs(this.modelDate);
        });
      }
    }
  }

  onFocusInput(): void {
    this.setAriaLabels()
  }

  setAriaLabels(): void {
    setTimeout(() => {
      const pCalendarElement = this.pCalendar.el.nativeElement;
      const prevButton = pCalendarElement.querySelector('.p-datepicker-prev');
      const nextButton = pCalendarElement.querySelector('.p-datepicker-next');
      const upHourButton = pCalendarElement.querySelector('.p-hour-picker .pi-chevron-up');
      const downHourButton = pCalendarElement.querySelector('.p-hour-picker .pi-chevron-down');
      const upMinuteButton = pCalendarElement.querySelector('.p-minute-picker .pi-chevron-up');
      const downMinuteButton = pCalendarElement.querySelector('.p-minute-picker .pi-chevron-down');

      if (prevButton) {
        prevButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_PREVIUS_MONTH'));
      }

      if (nextButton) {
        nextButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_NEXT_MONTH'));
      }

      if (upHourButton) {
        upHourButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_INCREASE_HOUR'));
      }

      if (downHourButton) {
        downHourButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_REDUCE_HOUR'));
      }

      if (upMinuteButton) {
        upMinuteButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_INCREASE_MINUTE'));
      }

      if (downMinuteButton) {
        downMinuteButton.setAttribute('aria-label', this.translateService.instant('CORE_DATEPICKER_REDUCE_MINUTE'));
      }
    }, 10);
  }

  ngAfterViewInit() {
    this.setClearIcon();

    // If the model comes with a date, it is set before ngAfterViewInit, so we have to check it to display the clear icon after creating it.
    if (this.modelDate) {
      this.displayClearIcon();
    }
  }

  setLogic(): void {
    this.isDesktop = this.responsiveService.isDesktop();
    this.showRuleErrorOutsideIcon = this.validationService.getShowRuleErrorOutsideIcon();
    this.readonlyInput = !this.isDesktop;
  }

  async setDatepickerI18nConfig(): Promise<void> {
    const primengI18n = await this.translateService.get('primeng').toPromise();
    this.firstDayOfWeek = primengI18n.firstDayOfWeek;
  }

  setClearIcon(): void {
    if (!this.showClear) {
      return;
    }

    this.clearIconElement = this.renderer.createElement('i');
    this.renderer.addClass(this.clearIconElement, 'icon-closex');
    this.renderer.addClass(this.clearIconElement, 'display-none');

    const pCalendarElement = this.pCalendar.el.nativeElement;
    const uiCalendarElement = pCalendarElement.querySelector('span.p-calendar');
    const uiCalendarIconElement = pCalendarElement.querySelector('.p-calendar-button');
    this.renderer.insertBefore(uiCalendarElement, this.clearIconElement, uiCalendarIconElement);
    this._listeners.push(
      this.renderer.listen(this.clearIconElement, 'click', (event) => this.onClickClearIcon(event))
    );
  }

  displayClearIcon(displayFlag = true): void {
    if (!this.clearIconElement) {
      return;
    }

    if (displayFlag) {
      this.renderer.removeClass(this.clearIconElement, 'display-none');
    } else {
      this.renderer.addClass(this.clearIconElement, 'display-none');
    }
  }

  onSelectDate(date): void {
    this.model = this.parseDateToMs(this.setTimeInDate(date));
    this.modelSelected.emit();
  }

  onCloseCalendar(): void {
    const dateSelected = this.validateDate(this.modelDate);
    if (dateSelected) {
      this.displayClearIcon();
    } else {
      this.modelChange.emit(null);
      this.displayClearIcon(false);
    }
  }

  onBlurInput(event): void {
    if (this.readonlyInput) {
      return;
    }

    if (event.target.value) {
      this.displayClearIcon();
      let date = this.pCalendar.parseDate(event.target.value, this.format);
      date = this.validateDate(date);
      if (new Date(this.model).setSeconds(0, 0) !== new Date(date).setSeconds(0, 0)) {
        this.modelChange.emit(this.parseDateToMs(this.setTimeInDate(date)));
      }
    }
  }

  onChangeInput(event): void {
    event.target.value = this.setSeparatorInStringDate(event.target.value);
    this.modelSelected.emit();
  }

  onClickClearIcon(event: Event): void {
    this.clearModel(event);
  }

  checkPreviousButtonAvailability(): void {
    const auxDate = moment(this.model).subtract(1, this.previousNextModeUnit).valueOf();
    this.isPreviousButtonAvailable = !!this.validateDate(this.parseMsToDate(auxDate));
  }

  onClickPrevious(): void {
    const auxDate = moment(this.model).subtract(1, this.previousNextModeUnit).valueOf();
    if (this.validateDate(this.parseMsToDate(auxDate))) {
      this.model = auxDate;
    }
  }

  checkNextButtonAvailability(): void {
    const auxDate = moment(this.model).add(1, this.previousNextModeUnit).valueOf();
    this.isNextButtonAvailable = !!this.validateDate(this.parseMsToDate(auxDate));
  }

  onClickNext(): void {
    const auxDate = moment(this.model).add(1, this.previousNextModeUnit).valueOf();
    if (this.validateDate(this.parseMsToDate(auxDate))) {
      this.model = auxDate;
    }
  }

  onClickToday(): void {
    this.setDateToday();
  }

  setDateToday(): void {
    if (this.minDate && moment(this.minDate) > moment(new Date())) {
      this.model = this.parseDateToMs(this.setTimeInDate(this.minDate));
    } else if (this.maxDate && moment(this.maxDate) < moment(new Date())) {
      this.model = this.parseDateToMs(this.setTimeInDate(this.maxDate));
    } else {
      this.model = this.parseDateToMs(this.setTimeInDate(new Date()));
    }
  }

  clearModel(event: Event): void {
    this.displayClearIcon(false);
    this.pCalendar.onClearButtonClick(event);
    this._model = null;
    this.modelChange.emit(null);
  }

  validateDate(date: Date): Date {
    if (!date || (this.minDate && this.minDate > date) || (this.maxDate && this.maxDate < date)) {
      return;
    }

    return date;
  }

  parseDateToMs(date: Date): number {
    if (!date) {
      return;
    }

    if (this.timeOnly) {
      return moment(date).valueOf() - moment().startOf('day').valueOf();
    }

    if (this.isUTC) {
      return GenericFunctionsUtil.userDateToUTCMs(date.getTime());
    }

    return date.getTime();
  }

  parseMsToDate(dateMs: number | Date): Date {
    if (!dateMs) {
      return;
    }

    if (this.timeOnly) {
      return new Date(+dateMs + moment().startOf('day').valueOf());
    }

    if (this.isUTC) {
      return new Date(GenericFunctionsUtil.UTCDateToUserDateMs(+dateMs));
    }

    return new Date(dateMs);
  }

  // Set default time with startOf, endOf.
  setTimeInDate(date: Date) {
    if (!date) {
      return;
    }

    // Only date: (!this.showTime && !this.timeOnly)
    // Date + time: (this.showTime && !this.defaultTimeSetFlag)
    // Only time: (this.timeOnly && !this.defaultTimeSetFlag)
    if (this.pCalendar && (this.startOf || this.endOf) &&
      ((!this.showTime && !this.timeOnly) || (this.showTime && !this.defaultTimeSetFlag) || (this.timeOnly && !this.defaultTimeSetFlag))) {

      this.defaultTimeSetFlag = true;

      if (this.startOf) {
        date = new Date(moment(date).startOf(this.startOf as unitOfTime.StartOf).valueOf());
      } else if (this.endOf) {
        date = new Date(moment(date).endOf(this.endOf as unitOfTime.StartOf).valueOf());
      }
    }

    return date;
  }

  setSeparatorInStringDate(date: string) {
    if (this.timeOnly) {
      return date.replace(/^(\d\d)(\d)$/g, '$1:$2');
    } else if (this.showTime) {
      return date
        .replace(/^(\d\d)(\d)$/g, '$1/$2')
        .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
        .replace(/^(\d\d\/\d\d\/\d\d\d\d\s\d\d)(\d+)$/g, '$1:$2')
        .replace(/[^\d\/\s\:]/g, ''); // TODO no deja borrar cuando has terminado de escribir todo
    } else if (this.format.length > 4 && this.format.length < 8) {
      return date
        .replace(/^(\d\d)(\d)$/g, '$1/$2')
        .replace(/[^\d\/]/g, '');
    } else {
      return date
        .replace(/^(\d\d)(\d)$/g, '$1/$2')
        .replace(/^(\d\d\/\d\d)(\d+)$/g, '$1/$2')
        .replace(/[^\d\/]/g, '');
    }
  }

  // Functions overwritting the real ones in the Calendar component.
  async decorateCalendar() {
    if (!this.pCalendar) {
      return;
    }

    // AlignOverlay overwritten to modify the top pixels of the overlay element because if not,
    // the blue animation cannot be seen.
    const originalAlignOverlay = this.pCalendar.alignOverlay.bind(this.pCalendar);
    this.pCalendar.alignOverlay = function () {
      originalAlignOverlay();
      if (this.overlay) {
        const top = this.overlay.style.top;
        const isOpenBottom = parseInt(top.substring(0, top.length - 2), 10) > 0;
        if (isOpenBottom) {
          this.overlay.style.top = (parseInt(top.substring(0, top.length - 2), 10) - 1).toString() + 'px';
        }
      }
    };

    // GetDateFormat overwritten to get the format set in Datepicker component.
    this.pCalendar.getDateFormat = () => {
      return this.format;
    };

    // FormatDate overwritten because the pCalendar does not accept localized formats like 'L'.
    const dateFormatPipe = new DateFormatPipe();
    this.pCalendar.formatDate = (date) => dateFormatPipe.transform(date, this.format);

    // FormatDateTime overwritten because the pCalendar append the time again after formatting the date.
    this.pCalendar.formatDateTime = function (date) {
      let formattedValue = null;
      if (date) {
        if (this.timeOnly) {
          formattedValue = this.formatTime(date);
        } else {
          formattedValue = this.formatDate(date);
        }
      }

      return formattedValue;
    };

    // ParseDateTime overwritten because we use own formats as 'L' or 'L LT', which are not supported by pCalendar.
    this.pCalendar.parseDateTime = function (text) {
      let date;
      const parts = text.split(' ');
      if (this.timeOnly) {
        date = new Date();
        this.populateTime(date, parts[0], parts[1]);
      } else {
        const dateFormat = this.getDateFormat();
        date = this.parseDate(text, dateFormat);
      }

      return date;
    };

    // ParseDate overwritten because we use own formats as 'L' or 'L LT', which are not supported by pCalendar.
    this.pCalendar.parseDate = (stringDate, format) => {
      if (moment(stringDate, format, true).isValid()) {
        return new Date(moment(stringDate, format, true).valueOf());
      }

      return null;
    };

    // UpdateUI overwritten to set seconds to zero if they are hidden.
    const originalUpdateUI = this.pCalendar.updateUI.bind(this.pCalendar);
    this.pCalendar.updateUI = function () {
      originalUpdateUI();
      if ((this.showTime || this.timeOnly) && !this.showSeconds) {
        this.currentSecond = 0;
      }
    };
  }

  onClickTooltipIcon(event: any): void {
    this.tooltipIconClick.emit(event);
  }

  ngOnDestroy(): void {
    this._listeners.forEach(fn => fn());
  }

}
